// src/config/api.ts
// 全 API エンドポイントを一元管理する。
// LEGACY_API: 旧 API（KANRI_ID ベース、段階的に廃止）
// NEW_API:    新 API（rl_book_id ベース、CloudFront 経由）
// STATIC_ASSETS: 静的アセット CDN

// ---------------------------------------------------------------------------
// LEGACY API — 旧 API（KANRI_ID ベース）
// ---------------------------------------------------------------------------
export const LEGACY_API = {
  // 著者詳細
  AUTHOR_DETAIL: 'https://d2c46f93hx1b7q.cloudfront.net/v1',

  // 著者情報（Wikipedia リンク等）— ManifestationDetailPage の関連書籍で使用
  AUTHORS_INFO: 'https://n3l8a26mj7.execute-api.us-east-1.amazonaws.com/default/readlinker-authors-info',

  // タイムライン
  TIMELINE: 'https://4u2j1o1jed.execute-api.us-east-1.amazonaws.com/default/readlinker-timeline-handler',

  // 書誌情報（認証付き） — **廃止**: 旧 /dp/:GlobalBookID ページ(BookDetailPage)専用だった。
  // 後継の /globalbook/:globalBookId（GlobalBookPage.tsx, NEW_API.BOOKS）を2026-08-23に実装済み
  // BIBLIO_FOR_REACT_AUTHORIZED: 'https://5ksv41zhte.execute-api.us-east-1.amazonaws.com/default/biblio-for-react-authorized',

  // お気に入り・履歴 — **廃止**: 新 API (NEW_API.FAVORITES) に移行済み
  // FAV_HISTORY_HANDLER: 'https://kypc7382xh.execute-api.us-east-1.amazonaws.com/default/readlinker-fav-history-handler',

  // ノート（認証付き）
  NOTES: 'https://95seh68s4g.execute-api.us-east-1.amazonaws.com/default/notes',

  // ノート（オープン）
  NOTES_OPEN: 'https://95seh68s4g.execute-api.us-east-1.amazonaws.com/default/note-open',

  // フィルタ済みユーザーノート（認証付き）
  FILTERED_USER_NOTES: 'https://6gxj8t1as1.execute-api.us-east-1.amazonaws.com/default/GetFilteredUserNotes',

  // フィルタ済みユーザーノート（オープン）
  FILTERED_USER_NOTES_OPEN: 'https://6gxj8t1as1.execute-api.us-east-1.amazonaws.com/default/GetFilteredUserNotesOpen',

  // フォロー情報
  FOLLOW_INFO: 'https://jbk4lskje4.execute-api.us-east-1.amazonaws.com/default/readlinker-get-follow_info',

  // 友達リスト
  FRIENDS_HANDLER: 'https://p19y7kh7qa.execute-api.us-east-1.amazonaws.com/default/readlinker-user-friendslist-handler',

  // Cognito 認証
  COGNITO_AUTH: 'https://p6718tdt10.execute-api.us-east-1.amazonaws.com/default/bublio-cognito-auth',

  // ユーザープロフィール（オープン）
  USERPROFILE_OPEN: 'https://cp04osziul.execute-api.us-east-1.amazonaws.com/default/readlinker-userprofile-open',

  // プロフィール画像変更
  PROFILE_IMAGE_CHANGE: 'https://moczww63k1.execute-api.us-east-1.amazonaws.com/default/readlinker-profile-image-change',

  // ユーザー情報（username → profile、CF経由）
  USER_INFO_BY_USERNAME_CF: 'https://d2l8a9ua2ipsnz.cloudfront.net/default',

  // ユーザー情報（userSub → profile、API Gateway）
  USER_INFO_BY_USERSUB: 'https://16z2rgasn7.execute-api.us-east-1.amazonaws.com/default/user',

  // OGP スクレイパー
  OGP_SCRAPER: 'https://z561znwcmb.execute-api.ap-northeast-1.amazonaws.com/default/',

  // OGP 画像生成 — **廃止**: 新 API (NEW_API.OGP) に移行済み
  // OGP_GENERATOR: 'https://d29nzmf96r2qm6.cloudfront.net/default/generate_ogp_http_param',
} as const;

// ---------------------------------------------------------------------------
// NEW API — 新 API（rl_book_id ベース、CloudFront 経由）
// ---------------------------------------------------------------------------
const API_BASE = 'https://api.readlinker.com';

export const NEW_API = {
  // 書誌取得（rl_book_id + source_id）
  BIB_UNIFIED: `${API_BASE}/bib`,

  // 書誌取得（rl_book_id のみ）
  BIB_SIMPLE: `${API_BASE}/bib`,

  // ISBN 検索
  ISBN_FETCH: `${API_BASE}/isbn`,

  // 典拠取得
  AUTHORITY_FETCH: `${API_BASE}/authority`,

  // ユーザーの本（CRUD + バルク処理）
  BOOKS: `${API_BASE}/books`,

  // 本棚
  SHELVES: `${API_BASE}/shelves`,

  // リスト
  LISTS: `${API_BASE}/lists`,

  // ハイライト
  HIGHLIGHTS: `${API_BASE}/highlights`,

  // インポート（外部サービスからのデータ取り込み）
  IMPORT: `${API_BASE}/import`,

  // エクスポート（登録本・抜粋メモのCSV書き出し）
  EXPORT: `${API_BASE}/export`,

  // プレフィックス検索
  PREFIX_SEARCH: `${API_BASE}/prefix`,

  // グラフエンジン
  GRAPH_ENGINE: `${API_BASE}/graph`,

  // お気に入り
  FAVORITES: `${API_BASE}/favorites`,

  // OGP 画像生成
  OGP: `${API_BASE}/ogp`,

  // QRシール生成
  STICKERS: `${API_BASE}/stickers`,

  // Admin管理画面（admin.readlinker.com専用、adminグループ必須）
  ADMIN: `${API_BASE}/admin`,

  // お知らせバナー（認証不要、一般ユーザー向け）
  ANNOUNCEMENT: `${API_BASE}/announcement`,
} as const;

// ---------------------------------------------------------------------------
// 静的アセット CDN
// ---------------------------------------------------------------------------
export const STATIC_ASSETS = {
  // プロフィール画像
  PROFILE_IMAGES: 'https://d2bgph94fruh44.cloudfront.net/profile_images',
} as const;

// ---------------------------------------------------------------------------
// ヘルパー関数
// ---------------------------------------------------------------------------

/** 書誌取得（新 API）: rl_book_id + source_id */
export const buildBibUnifiedUrl = (rlBookId: number, sourceId: string) =>
  `${NEW_API.BIB_UNIFIED}/${rlBookId}/${sourceId}`;

/** 書誌取得（新 API）: rl_book_id のみ */
export const buildBibSimpleUrl = (rlBookId: number) =>
  `${NEW_API.BIB_SIMPLE}/${rlBookId}`;

/** ISBN 検索（新 API） */
export const buildIsbnFetchUrl = (isbn: string) =>
  `${NEW_API.ISBN_FETCH}/${isbn}`;

/** プレフィックス検索（新 API）。bookCursor を渡すと書籍の続き（51件目以降）を取得する */
export const buildPrefixSearchUrl = (query: string, bookLimit = 20, authorLimit = 20, bookCursor?: string) => {
  const base = `${NEW_API.PREFIX_SEARCH}?q=${encodeURIComponent(query)}&book_limit=${bookLimit}&author_limit=${authorLimit}`;
  return bookCursor ? `${base}&book_cursor=${encodeURIComponent(bookCursor)}` : base;
};

/** 典拠取得（新 API） */
export const buildAuthorityFetchUrl = (tenkyoId: string) =>
  `${NEW_API.AUTHORITY_FETCH}/${tenkyoId}`;

/** 著者詳細（旧 API） */
export const buildAuthorDetailUrl = (tenkyoId: string, queryParams?: string) =>
  `${LEGACY_API.AUTHOR_DETAIL}/${tenkyoId}${queryParams ? `?${queryParams}` : ''}`;

/** ユーザー情報（username ベース、CF経由） */
export const buildUserInfoByUsernameCFUrl = (username: string) =>
  `${LEGACY_API.USER_INFO_BY_USERNAME_CF}/${username}`;

/** ユーザー情報（userSub ベース） */
export const buildUserInfoByUsersubUrl = (userSub: string) =>
  `${LEGACY_API.USER_INFO_BY_USERSUB}/${userSub}`;

/** OGP 画像 URL（rl_book_id ベース） */
export const buildOgpUrlByRlBookId = (rlBookId: number) =>
  `${NEW_API.OGP}?rl_book_id=${rlBookId}`;

/** OGP 画像 URL（タイトルベース） */
export const buildOgpUrlByTitle = (title: string, isbn?: string) => {
  let url = `${NEW_API.OGP}?title=${encodeURIComponent(title)}`;
  if (isbn) {
    url += `&isbn=${isbn}`;
  }
  return url;
};

/** プロフィール画像 URL */
export const buildProfileImageUrl = (fileName: string) =>
  `${STATIC_ASSETS.PROFILE_IMAGES}/${fileName}`;
